import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Pause, Pencil, ScrollText, X, RotateCw, Loader2, PauseCircle, PlayCircle } from "lucide-react";
import { api } from "@/lib/api";
import { BASE, call, get, nz, Pill, freqText, WEEKDAYS } from "./common";

export default function SchedulerPage() {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const [logJob, setLogJob] = useState(null);
  const load = useCallback(() => get("/scheduler").then(setD), []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  const act = async (path, msg) => { if (await call(() => api.post(`${BASE}${path}`), msg)) load(); };
  if (!d) return <p className="text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-5" data-testid="scheduler-page">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted-foreground flex-1">All times Pacific/Auckland. One central queue: a job waits while another supplier job (or the Goodson/dealer scraper) holds the runner.</p>
        {d.paused
          ? <Button onClick={() => act("/queue/resume", "Queue resumed")} className="bg-emerald-600 text-white" data-testid="queue-resume-btn"><PlayCircle size={16} className="mr-1" />Resume queue</Button>
          : <Button variant="outline" onClick={() => act("/queue/pause", "Queue paused")} data-testid="queue-pause-btn"><PauseCircle size={16} className="mr-1" />Pause queue</Button>}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <section className={`rounded-xl border-2 p-4 ${d.running.length || d.lock ? "border-amber-400 bg-amber-50" : "bg-white"}`} data-testid="scheduler-running">
          <h3 className="font-bold mb-2 flex items-center gap-2">{(d.running.length || d.lock) && <Loader2 size={16} className="animate-spin text-amber-600" />}Running now</h3>
          {d.running.length === 0 && !d.lock && <p className="text-sm text-muted-foreground">Nothing running{d.paused ? " — queue is paused" : ""}.</p>}
          {d.running.length === 0 && d.lock && <p className="text-sm" data-testid="scheduler-lock-label">Runner held by <b>{d.lock.label}</b> since {nz(d.lock.since)}</p>}
          {d.running.map((j) => <JobRow key={j.id} j={j} onLog={setLogJob} onCancel={() => act(`/jobs/${j.id}/cancel`, "Cancel requested")} />)}
        </section>
        <section className="rounded-xl border bg-white p-4" data-testid="scheduler-queued">
          <h3 className="font-bold mb-2">Queued ({d.queued.length})</h3>
          {d.queued.length === 0 && <p className="text-sm text-muted-foreground">Queue is empty.</p>}
          {d.queued.map((j) => <JobRow key={j.id} j={j} onLog={setLogJob} onCancel={() => act(`/jobs/${j.id}/cancel`, "Job cancelled")} />)}
        </section>
      </div>
      <section className="rounded-xl border bg-white overflow-x-auto" data-testid="scheduler-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="p-3">Supplier</th><th className="p-3">Job type</th><th className="p-3">Frequency</th><th className="p-3">Last run</th><th className="p-3">Next run</th><th className="p-3">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody className="divide-y">
            {d.schedules.map((s) => (
              <tr key={s.id} data-testid={`schedule-row-${s.supplier_name}-${s.type}`}>
                <td className="p-3 font-semibold">{s.supplier_name}</td><td className="p-3">{s.label}</td>
                <td className="p-3">{freqText(s)}</td>
                <td className="p-3 text-xs">{nz(s.last_run_at)} {s.last_status && <Pill s={s.last_status} />}</td>
                <td className="p-3 text-xs" data-testid={`schedule-next-${s.supplier_name}-${s.type}`}>{s.enabled && !s.paused ? nz(s.next_run_at) : "—"}</td>
                <td className="p-3"><Pill s={!s.enabled ? "cancelled" : s.paused ? "partial" : "completed"} label={!s.enabled ? "Disabled" : s.paused ? "Paused" : "Enabled"} testid={`schedule-status-${s.supplier_name}-${s.type}`} /></td>
                <td className="p-3"><div className="flex gap-1 justify-end">
                  <Button size="sm" variant="outline" onClick={() => act(`/schedules/${s.id}/run-now`, (r) => (r.duplicate ? "Already queued" : "Queued"))} data-testid={`schedule-run-${s.type}-${s.supplier_name}`}><Play size={13} className="mr-1" />Run now</Button>
                  <Button size="sm" variant="outline" onClick={() => act(`/schedules/${s.id}/${s.paused ? "resume" : "pause"}`, s.paused ? "Schedule resumed" : "Schedule paused")} data-testid={`schedule-pause-${s.type}-${s.supplier_name}`}>
                    {s.paused ? <Play size={13} /> : <Pause size={13} />}<span className="ml-1">{s.paused ? "Resume" : "Pause"}</span></Button>
                  <Button size="sm" variant="outline" onClick={() => setEdit(s)} data-testid={`schedule-edit-${s.type}-${s.supplier_name}`}><Pencil size={13} className="mr-1" />Edit</Button>
                </div></td>
              </tr>))}
            {d.legacy.map((l) => (
              <tr key={l.name} className="bg-slate-50/60" data-testid={`legacy-row-${l.name}`}>
                <td className="p-3 font-semibold">{l.name}</td><td className="p-3">Portal scraper</td><td className="p-3">{freqText(l)}</td>
                <td className="p-3 text-xs">{nz(l.last_run)} {l.last_status && <Pill s={l.last_status} />}</td><td className="p-3 text-xs">{l.enabled ? nz(l.next_run_at) : "—"}</td>
                <td className="p-3 text-xs">{l.enabled ? "Enabled" : "Disabled"}</td><td className="p-3 text-xs text-muted-foreground text-right">Edit time in Supplier Scrapers · shares the runner lock</td>
              </tr>))}
          </tbody>
        </table>
      </section>
      <section className="rounded-xl border bg-white p-4" data-testid="scheduler-recent">
        <h3 className="font-bold mb-2">Recent jobs</h3>
        {d.recent.length === 0 && <p className="text-sm text-muted-foreground">No finished jobs yet.</p>}
        {d.recent.map((j) => <JobRow key={j.id} j={j} onLog={setLogJob} onRetry={["failed", "timed_out", "cancelled"].includes(j.status) ? () => act(`/jobs/${j.id}/retry`, "Retry queued") : null} />)}
      </section>
      <EditSchedule s={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
      <JobLogDialog id={logJob} onClose={() => setLogJob(null)} />
    </div>
  );
}

function JobRow({ j, onLog, onCancel, onRetry }) {
  return (
    <div className="py-2 border-b last:border-0 flex flex-wrap items-center gap-2 text-sm" data-testid={`job-row-${j.id}`}>
      <Pill s={j.status} /> <b>{j.supplier_name}</b> · {j.label}
      <span className="text-xs text-muted-foreground">{nz(j.finished_at || j.started_at || j.created_at)} · {j.trigger}{j.attempts > 1 ? ` · attempt ${j.attempts}` : ""}{j.duration_s ? ` · ${j.duration_s}s` : ""}</span>
      {j.progress?.total ? <span className="text-xs font-mono">{j.progress.done}/{j.progress.total}</span> : null}
      <span className="ml-auto flex gap-1">
        <Button size="sm" variant="ghost" onClick={() => onLog(j.id)} data-testid={`job-log-${j.id}`}><ScrollText size={13} className="mr-1" />Logs</Button>
        {onCancel && <Button size="sm" variant="ghost" onClick={onCancel} data-testid={`job-cancel-${j.id}`}><X size={13} className="mr-1" />Cancel</Button>}
        {onRetry && <Button size="sm" variant="ghost" onClick={onRetry} data-testid={`job-retry-${j.id}`}><RotateCw size={13} className="mr-1" />Retry</Button>}
      </span>
      {j.error && <p className="w-full text-xs text-red-600">{j.error}</p>}
    </div>
  );
}

export function JobLogDialog({ id, onClose }) {
  const [j, setJ] = useState(null);
  useEffect(() => {
    if (!id) { setJ(null); return undefined; }
    const f = () => get(`/jobs/${id}`).then(setJ);
    f();
    const t = setInterval(f, 4000);
    return () => clearInterval(t);
  }, [id]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="job-log-dialog">
        <DialogHeader><DialogTitle>{j ? `${j.supplier_name} · ${j.label}` : "Job log"}</DialogTitle>
          <DialogDescription>{j && <><Pill s={j.status} /> {j.error}</>}</DialogDescription></DialogHeader>
        <pre className="bg-slate-900 text-slate-100 text-xs rounded-lg p-3 max-h-96 overflow-y-auto whitespace-pre-wrap" data-testid="job-log-lines">{(j?.log || []).join("\n") || "No log lines yet."}</pre>
        {j?.result && <pre className="text-xs bg-slate-50 rounded p-2 whitespace-pre-wrap" data-testid="job-log-result">{JSON.stringify(j.result, null, 1)}</pre>}
      </DialogContent>
    </Dialog>
  );
}

function EditSchedule({ s, onClose, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => { setF(s ? { ...s } : null); }, [s]);
  if (!f) return null;
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    const body = Object.fromEntries(["enabled", "frequency", "time", "weekday", "interval_hours", "max_runtime_min", "request_delay_ms", "max_retries"].map((k) => [k, f[k]]));
    if (await call(() => api.put(`${BASE}/schedules/${f.id}`, body), "Schedule saved")) onSaved();
  };
  const N = (k, label) => <div><Label className="text-xs">{label}</Label><Input type="number" value={f[k]} onChange={(e) => set(k, Number(e.target.value))} data-testid={`sched-${k}`} /></div>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="schedule-edit-dialog">
        <DialogHeader><DialogTitle>Edit schedule — {f.supplier_name} {f.label}</DialogTitle>
          <DialogDescription>Time zone: Pacific/Auckland. Give each supplier its own time slot; overlapping jobs simply queue.</DialogDescription></DialogHeader>
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.enabled} onCheckedChange={(c) => set("enabled", c)} data-testid="sched-enabled" />Schedule enabled</label>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Frequency</Label>
            <Select value={f.frequency} onValueChange={(v) => set("frequency", v)}>
              <SelectTrigger data-testid="sched-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>{[["hourly", "Hourly"], ["daily", "Daily"], ["weekly", "Weekly"], ["custom", "Custom (every N hours)"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Preferred start time</Label><Input type="time" value={f.time} onChange={(e) => set("time", e.target.value)} data-testid="sched-time" /></div>
          {f.frequency === "weekly" && <div><Label className="text-xs">Day</Label>
            <Select value={String(f.weekday)} onValueChange={(v) => set("weekday", Number(v))}>
              <SelectTrigger data-testid="sched-weekday"><SelectValue /></SelectTrigger>
              <SelectContent>{WEEKDAYS.map((w, i) => <SelectItem key={w} value={String(i)}>{w}</SelectItem>)}</SelectContent>
            </Select></div>}
          {f.frequency === "custom" && N("interval_hours", "Every N hours")}
          {N("max_runtime_min", "Maximum runtime (min)")}
          {N("request_delay_ms", "Delay between requests (ms)")}
          {N("max_retries", "Maximum retry attempts")}
        </div>
        <Button onClick={save} className="bg-steel text-white" data-testid="sched-save">Save schedule</Button>
      </DialogContent>
    </Dialog>
  );
}
